import { useEffect, useMemo, useRef } from "react";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { setAiHighlightedDataObjects } from "store/dataObjects/actions";
import { filterDataObjects, type FilterCriterion } from "./match";
import type { ToolPartProps } from "./types";

interface FindDataObjectsInput {
    criteria: FilterCriterion[];
    description: string;
}

interface FindDataObjectsOutput {
    matchCount: number;
    dataObjectIds: string[];
    description: string;
}

export default function FindDataObjectsTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<FindDataObjectsInput, FindDataObjectsOutput>) {
    const { addToolOutput, dispatch, dataObjects, schemaFields } = helpers;
    const resolvedRef = useRef(false);

    const matches = useMemo(() => {
        if (!input || !Array.isArray(input.criteria)) return [];
        return filterDataObjects(dataObjects, schemaFields, input.criteria);
    }, [input, dataObjects, schemaFields]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        resolvedRef.current = true;
        const ids = matches.map((m) => m.dataObjectId);
        dispatch(setAiHighlightedDataObjects(ids));
        addToolOutput({
            tool: "findDataObjects",
            toolCallId,
            output: {
                matchCount: ids.length,
                dataObjectIds: ids,
                description: input?.description || "",
            },
        });
    }, [state, matches, dispatch, addToolOutput, toolCallId, input]);

    const ids = state === "output-available" && output ? output.dataObjectIds : matches.map((m) => m.dataObjectId);
    const matchCount = state === "output-available" && output ? output.matchCount : matches.length;
    const description = (state === "output-available" && output?.description) || input?.description || "";

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#001D3D]">
                <SearchOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Filter results</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            <div className="mt-1 text-xs text-zinc-500">
                {matchCount === 0 ? "No matching records." : `${matchCount} record${matchCount === 1 ? "" : "s"} highlighted in the list.`}
            </div>
            {ids.length > 0 && ids.length <= 20 && (
                <div className="mt-1 break-all text-[10px] text-zinc-400">ids: {ids.join(", ")}</div>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
